import { useEffect, useRef, useState } from 'react';
import { Button } from '@indipia/ui-core';
import { loadNaverMaps, type NaverMap } from './naverMaps';

const NAVER_MAP_CLIENT_ID = String(import.meta.env.VITE_NAVER_MAP_CLIENT_ID ?? '');
// 좌표 미확보 시 폴백 센터 (서울시청). 정상 흐름에서는 도로명 주소 좌표를 사용한다.
const FALLBACK_CENTER = { lat: 37.5665, lng: 126.978 };

interface StepMapProps {
  /** 기 입력한 도로명 주소 (안내 문구용) */
  roadAddress: string;
  /** 초기 센터 좌표 (도로명 주소 지오코딩 또는 기존 저장 좌표) */
  initialLat?: number;
  initialLng?: number;
  /** '이전' 버튼. 미지정 시 버튼 숨김 (수정 팝업 등 단독 사용) */
  onPrev?: () => void;
  /** 핀 위치 확정 시 확정 좌표 전달 */
  onConfirm: (coord: { lat: number; lng: number }) => void;
  /** 확정 버튼 문구 (기본 '이 위치로 저장') */
  confirmLabel?: string;
  /** 저장 진행 중 — 버튼 비활성/문구 처리 */
  submitting?: boolean;
}

/**
 * 지도 핀 단계 (현장 등록/수정 공용)
 *
 * - 도로명 주소 기준 좌표를 센터로 네이버 지도를 표시
 * - 핀 아이콘은 지도 중앙에 고정(오버레이). 지도를 드래그하면 지도만 움직이고 핀은 중앙 유지
 * - 지도 중심 = 확정 좌표. '확정' 시 동 단위 위경도를 onConfirm으로 넘긴다.
 */
export function StepMap({
  roadAddress,
  initialLat,
  initialLng,
  onPrev,
  onConfirm,
  confirmLabel = '이 위치로 저장',
  submitting = false,
}: StepMapProps) {
  const mapElRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<NaverMap | null>(null);

  const startLat = initialLat ?? FALLBACK_CENTER.lat;
  const startLng = initialLng ?? FALLBACK_CENTER.lng;

  const [center, setCenter] = useState({ lat: startLat, lng: startLng });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    if (!NAVER_MAP_CLIENT_ID) {
      setError('네이버 지도 키(VITE_NAVER_MAP_CLIENT_ID)가 설정되지 않았습니다.');
      setLoading(false);
      return;
    }

    loadNaverMaps(NAVER_MAP_CLIENT_ID)
      .then(() => {
        if (cancelled || !mapElRef.current || !window.naver) return;
        const { maps } = window.naver;
        const map = new maps.Map(mapElRef.current, {
          center: new maps.LatLng(startLat, startLng),
          zoom: 17,
        });
        mapRef.current = map;
        // 지도 이동이 멈출 때마다 중심 좌표 갱신 (핀은 중앙 고정 오버레이)
        maps.Event.addListener(map, 'idle', () => {
          const c = map.getCenter();
          setCenter({ lat: c.lat(), lng: c.lng() });
        });
        setLoading(false);
      })
      .catch((e: Error) => {
        if (!cancelled) {
          setError(e.message);
          setLoading(false);
        }
      });

    return () => {
      cancelled = true;
      mapRef.current?.destroy?.();
      mapRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="space-y-4">

      {/* 안내 */}
      <p className="text-xs text-text-secondary">
        입력한 주소 <strong className="text-text-primary">{roadAddress}</strong> 위치입니다.
        지도를 움직여 <strong className="text-text-primary">핀</strong>을 원하는 동 위치에 맞춰주세요.
      </p>

      {/* 지도 + 중앙 고정 핀 */}
      <div className="relative h-72 w-full overflow-hidden rounded-xl border border-border-default bg-surface-subtle">
        <div ref={mapElRef} className="h-full w-full" />

        {/* 중앙 고정 핀 — pointer-events 없음(드래그는 지도로 전달), 핀 끝이 정중앙을 가리킴 */}
        {!error && !loading && (
          <div className="pointer-events-none absolute left-1/2 top-1/2 z-dropdown -translate-x-1/2 -translate-y-full">
            <svg width="30" height="40" viewBox="0 0 24 32" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path
                d="M12 0C5.373 0 0 5.373 0 12c0 8.4 12 20 12 20s12-11.6 12-20C24 5.373 18.627 0 12 0z"
                className="fill-primary-500"
              />
              <circle cx="12" cy="12" r="4.5" className="fill-white" />
            </svg>
          </div>
        )}

        {/* 로딩 / 에러 오버레이 */}
        {(loading || error) && (
          <div className="absolute inset-0 flex items-center justify-center px-6 text-center">
            <p className={`text-xs ${error ? 'text-danger' : 'text-text-secondary'}`}>
              {error ?? '지도 불러오는 중…'}
            </p>
          </div>
        )}
      </div>

      {/* 현재 중심 좌표 (실시간) */}
      <div className="rounded-xxl bg-surface-subtle px-3 py-2.5">
        <p className="text-caption font-semibold text-text-secondary">현재 지도 중심 좌표</p>
        <p className="mt-0.5 font-mono text-xs text-text-primary">
          위도 {center.lat.toFixed(6)} · 경도 {center.lng.toFixed(6)}
        </p>
      </div>

      {/* 하단 버튼 */}
      <div className="flex gap-2 pt-1">
        {onPrev && (
          <Button variant="secondary" size="lg" type="button" onClick={onPrev} disabled={submitting} className="flex-none">
            ← 이전
          </Button>
        )}
        <Button
          variant="primary"
          size="lg"
          type="button"
          disabled={loading || !!error || submitting}
          onClick={() => onConfirm({ ...center })}
          className="flex-1"
        >
          {submitting ? '저장 중…' : confirmLabel}
        </Button>
      </div>
    </div>
  );
}
